/**
 * Section 13 — TradingView Setup.
 *
 * End-to-end guide for wiring TradingView Pine alerts into earngush.
 * Covers per-user webhook URL + X-Webhook-Secret header (R80 model),
 * the JSR_CLR pipe message format, alert message templates for common
 * setups, In-House Signal Mode, and a verification step using the
 * Alerts page Test Webhook button.
 */
import React from 'react';
import { Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, List, ListItem, ListItemText } from '@mui/material';
import WebhookIcon from '@mui/icons-material/Webhook';
import GuideCallout from '../GuideCallout';
import GuideExample from '../GuideExample';
import GuideCodeBlock from '../GuideCodeBlock';
import OpenInApp from '../OpenInApp';
import { GuideSection } from './types';

const H = ({ id, children }: { id: string; children: React.ReactNode }) => (
  <Typography id={`anchor-${id}`} variant="h5" sx={{ fontWeight: 700, mt: 4, mb: 1.25, scrollMarginTop: 80 }}>{children}</Typography>
);
const P = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="body1" sx={{ mb: 1.25, lineHeight: 1.65, fontSize: '0.95rem' }}>{children}</Typography>
);

const Section_tradingview_setup: GuideSection = {
  id: 'tradingview-setup',
  label: 'TradingView Setup',
  group: 'setup',
  icon: <WebhookIcon fontSize="small" />,
  render: () => (
    <>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>TradingView Setup</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        Wire your TradingView Pine alerts into earngush. ~10 minutes end-to-end. Requires a TradingView
        plan that supports webhook alerts (Essential and above).
      </Typography>

      <H id="step-1-secret">Step 1 — Get your webhook URL + secret</H>
      <P>
        Go to <OpenInApp to="/settings" label="Settings → Broker Credentials" /> and find the
        <strong> TradingView Webhook URL</strong> card. It shows:
      </P>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Webhook URL</strong>: looks like <code>https://earngush.com/webhook/tradingview?userId=N&amp;secret=…</code>. Use the same URL for all your TradingView alerts.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Secret</strong>: the part after <code>secret=</code>, a 64-character token that belongs to your account only. Treat it like a password: anyone who has it can send alerts that trade on your account.</>} />
        </ListItem>
      </List>
      <P>
        <strong>Show</strong> reveals the secret, and the copy icon copies the whole URL.
        <strong> New secret</strong> issues a fresh one, and the old one stops working at once, so every
        TradingView alert then needs the new URL.
      </P>
      <GuideCallout kind="danger" title="The secret is yours alone">
        There is no shared or admin secret. A URL only works for the account whose User ID
        and secret it carries. The old platform-wide secret was removed in November 2026.
      </GuideCallout>

      <H id="step-2-alert">Step 2 — Configure the TradingView alert</H>
      <P>
        In TradingView, on the chart of the symbol you want to trade, click the alarm-clock icon to open
        the alert dialog. Two key fields:
      </P>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
          <ListItemText primary={<><strong>Webhook URL</strong> — paste the URL from Step 1.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
          <ListItemText primary={<><strong>Custom HTTP Headers</strong> (available on Premium plans) — add a header:<br /><code>X-Webhook-Secret: &lt;your-secret&gt;</code></>} />
        </ListItem>
      </List>
      <GuideCallout kind="info" title="No custom headers on your plan?">
        TradingView Essential / Plus tiers don't expose the custom-header field. As a fallback, append
        <code> &amp;secret=&lt;your-secret&gt;</code> to the URL query string. This works but the secret
        will appear in proxy access logs — header form is preferred.
      </GuideCallout>

      <H id="step-3-message">Step 3 — Write the alert message (JSR_CLR pipe format)</H>
      <P>
        earngush parses pipe-delimited messages of the form:
      </P>
      <GuideCodeBlock label="JSR_CLR alert message format">
{`<STRATEGY>|TICKER=<symbol>|TF=<interval>|SIGNAL=<action>|ENTRY_LEVEL=<price>|ISL=<sl>|TGT=<target>|STRENGTH=<H|M|L>|CONFIRMED=<Y|N>`}
      </GuideCodeBlock>
      <P>
        Field reference:
      </P>
      <Table size="small" sx={{ my: 1 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Field</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Required?</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Notes</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow><TableCell><code>&lt;STRATEGY&gt;</code></TableCell><TableCell>Y (first segment)</TableCell><TableCell>Any string. Recorded as <code>strategy</code> on the alert. Use a stable name per Pine script (e.g. "MySniper", "MeanRev_v3"). Use literal <code>JSR_CLR</code> for backward compat with older docs.</TableCell></TableRow>
          <TableRow><TableCell><code>TICKER</code></TableCell><TableCell>Y</TableCell><TableCell>The instrument symbol. Use TradingView's <code>{`{{ticker}}`}</code> placeholder so it matches the chart symbol automatically.</TableCell></TableRow>
          <TableRow><TableCell><code>TF</code></TableCell><TableCell>Recommended</TableCell><TableCell>The chart interval. Use <code>{`{{interval}}`}</code>.</TableCell></TableRow>
          <TableRow><TableCell><code>SIGNAL</code></TableCell><TableCell>Y (or NOTIFY)</TableCell><TableCell>BUY / SHORT / SELL / COVER. Or use NOTIFY for informational-only alerts.</TableCell></TableRow>
          <TableRow><TableCell><code>ENTRY_LEVEL</code></TableCell><TableCell>Recommended</TableCell><TableCell>Suggested entry price. Use <code>{`{{close}}`}</code> for current-bar close. If omitted, the platform fills with current market price.</TableCell></TableRow>
          <TableRow><TableCell><code>ISL</code></TableCell><TableCell>Recommended</TableCell><TableCell>Initial Stop Loss. Common patterns: <code>{`{{low}}`}</code> for longs, <code>{`{{high}}`}</code> for shorts, or a computed value from your Pine indicator.</TableCell></TableRow>
          <TableRow><TableCell><code>TGT</code></TableCell><TableCell>Optional</TableCell><TableCell>Take-profit price. Omit if you only want the SL planted.</TableCell></TableRow>
          <TableRow><TableCell><code>STRENGTH</code></TableCell><TableCell>Optional</TableCell><TableCell>HIGH / MEDIUM / LOW (or legacy 3/2/1). Drives renotify + speech / Telegram chime rules.</TableCell></TableRow>
          <TableRow><TableCell><code>CONFIRMED</code></TableCell><TableCell>Recommended</TableCell><TableCell>Y for closed-bar verdict; N for mid-bar preview. Auto Trade only fires on Y.</TableCell></TableRow>
          <TableRow><TableCell><code>EXIT_REASON</code></TableCell><TableCell>Optional (exits)</TableCell><TableCell>For SELL / COVER alerts. E.g. <code>REVERSE_SIGNAL</code>, <code>ISL_BREACH</code>, <code>TGT_HIT</code>.</TableCell></TableRow>
        </TableBody>
      </Table>

      <H id="alert-templates">Common alert templates</H>

      <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 2 }}>Long entry (basic)</Typography>
      <GuideCodeBlock label="Bullish entry">
{`JSR_CLR|TICKER={{ticker}}|TF={{interval}}|SIGNAL=BUY|ENTRY_LEVEL={{close}}|ISL={{low}}|STRENGTH=MEDIUM|CONFIRMED=Y`}
      </GuideCodeBlock>

      <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 2 }}>Short entry with target</Typography>
      <GuideCodeBlock label="Bearish entry">
{`JSR_CLR|TICKER={{ticker}}|TF={{interval}}|SIGNAL=SHORT|ENTRY_LEVEL={{close}}|ISL={{high}}|TGT={{close}}*0.98|STRENGTH=HIGH|CONFIRMED=Y`}
      </GuideCodeBlock>

      <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 2 }}>Exit signal — close long position</Typography>
      <GuideCodeBlock label="Long exit">
{`JSR_CLR|TICKER={{ticker}}|TF={{interval}}|SIGNAL=SELL|EXIT_REASON=REVERSE_SIGNAL|SL={{close}}|CONFIRMED=Y`}
      </GuideCodeBlock>

      <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 2 }}>Information-only notification (no order)</Typography>
      <GuideCodeBlock label="Envelope touch notify">
{`JSR_CLR|TICKER={{ticker}}|TF={{interval}}|NOTIFY=ENV_LONG_TOUCH_NEW|HA_HIGH={{high}}|ENV_UPPER={{plot("Env Upper")}}`}
      </GuideCodeBlock>
      <P>
        NOTIFY alerts skip the order path entirely — they show up in the Alerts list with an info tag for
        your awareness, no broker call.
      </P>

      <H id="step-4-test">Step 4 — Verify with Test Webhook</H>
      <P>
        Open <OpenInApp to="/alerts" label="Alerts" /> and click the Test Webhook button (top right).
        Fires a synthetic alert against your own webhook URL using your per-user secret. If everything's
        wired correctly, you'll see a new row appear within ~1 second with source=TEST.
      </P>
      <P>
        Common test-failure causes:
      </P>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary="Wrong X-Webhook-Secret value — 401 in the Logs. Re-copy the secret from Settings → Webhooks." />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary="Symbol not in your Instruments table — alert is received but no trade can be created. Add the symbol via Instruments page or by importing from your broker." />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary="Webhook firing but no order — check Auto Trade ON state + check CONFIRMED=Y on the alert + check the daily-loss cap isn't breached." />
        </ListItem>
      </List>

      <H id="in-house-mode">In-House Signal Mode</H>
      <P>
        Toggle in <OpenInApp to="/settings" label="Settings → Trading Preferences" />. When ON:
      </P>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary="TradingView webhooks are accepted (return 200) but silently ignored." />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary="Only the in-house Sniper / RockBand / DailyBlast strategies emit actionable alerts." />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary="Useful when you've moved entirely to in-house signals and want to mute TradingView without disabling individual alerts on the TradingView side." />
        </ListItem>
      </List>

      <H id="plain-text">Plain-text alert formats (legacy)</H>
      <P>
        For very simple use cases, earngush also parses two plain-text patterns (no Pine knowledge needed):
      </P>
      <GuideCodeBlock label="Crossing pattern">
{`BTCUSD.P Crossing Up 80000`}
      </GuideCodeBlock>
      <GuideCodeBlock label="Greater/Less Than pattern">
{`ETHUSD.P Greater Than 3500`}
      </GuideCodeBlock>
      <P>
        These translate to BUY (Crossing Up / Less Than) or SELL (Crossing Down / Greater Than). Useful for
        TradingView's built-in price-cross alerts that don't need a custom Pine script. The{' '}
        <code>userId</code> in the URL is honoured here too (since the body has no userId).
      </P>

      <GuideExample title="A full Pine-to-earngush wiring (Sniper-style alert)">
        On TradingView, you have a Pine script that prints a green diamond when CMid flips bullish.<br />
        Alert dialog:<br />
        &nbsp;&nbsp;Condition: <em>My CMid Strategy → buy signal</em><br />
        &nbsp;&nbsp;Webhook URL: <code>https://earngush.com/v1/webhook/tradingview?userId=5</code><br />
        &nbsp;&nbsp;Custom header: <code>X-Webhook-Secret: 8a3f4c2…64-char-secret…</code><br />
        &nbsp;&nbsp;Message:
        <Box component="pre" sx={{ fontFamily: 'ui-monospace, monospace', fontSize: '0.78rem', p: 1, my: 0.5, bgcolor: 'rgba(0,0,0,0.05)', borderRadius: 0.5 }}>{`MyCMid|TICKER={{ticker}}|TF={{interval}}|SIGNAL=BUY|ENTRY_LEVEL={{close}}|ISL={{low}}|TGT={{close}}*1.005|STRENGTH=HIGH|CONFIRMED=Y`}</Box>
        Save. Next time CMid flips on the chart, the alert fires → earngush parses → if Auto Trade is ON,
        the order goes straight to your broker. If OFF, it sits in Recent Alerts for one-click execute.
      </GuideExample>
    </>
  ),
  searchIndex: [
    { heading: 'Get webhook URL + secret', anchor: 'step-1-secret', body: 'webhook url secret per user x webhook secret rotate' },
    { heading: 'Configure TradingView alert', anchor: 'step-2-alert', body: 'configure tradingview alert webhook url custom header' },
    { heading: 'JSR_CLR alert message format', anchor: 'step-3-message', body: 'jsr clr alert message format ticker tf signal entry isl tgt strength confirmed' },
    { heading: 'Common alert templates',    anchor: 'alert-templates', body: 'alert templates long short exit notify' },
    { heading: 'Verify with Test Webhook',  anchor: 'step-4-test', body: 'verify test webhook synthetic alert' },
    { heading: 'In-House Signal Mode',      anchor: 'in-house-mode', body: 'in house signal mode mute tradingview' },
    { heading: 'Plain-text alert formats',  anchor: 'plain-text', body: 'plain text format crossing greater less than' },
  ],
};

export default Section_tradingview_setup;
